import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/field";
import {
  Select,
  SelectItem,
  SelectList,
  SelectTrigger,
} from "@/components/ui/select";
import { toast } from "@/components/ui/toast";
import { getElectronAPI, isElectron } from "@/lib/electron";
import { setElectronSelectedPrinterId } from "@/lib/electron-config";
import { toPrinterTarget } from "@/lib/labels/electron-print";
import { useWorkstationPrinter } from "@/lib/labels/useElectronPrinter";

export function WorkstationPrinterSettings() {
  if (!isElectron()) return null;

  return <WorkstationPrinterSettingsInner />;
}

function WorkstationPrinterSettingsInner() {
  const { printers, selectedId, selectedPrinter, setSelectedId } =
    useWorkstationPrinter();
  const [testing, setTesting] = useState(false);

  const printerOptions =
    printers?.map((p) => ({
      id: p.id,
      enabled: p.enabled,
      label: `${p.label ? `${p.name} (${p.label})` : p.name}${p.enabled ? "" : " (disabled)"}`,
    })) ?? [];
  const disabledPrinterIds = printerOptions
    .filter((printer) => !printer.enabled)
    .map((printer) => printer.id);

  const api = getElectronAPI();
  const handleSelectionChange = async (key: string | number | null) => {
    const id = key ? Number(key) : null;
    const match = id ? printers?.find((p) => p.id === id) : null;
    if (match && !match.enabled) {
      toast.error("Printer is disabled");
      return;
    }

    const previousId = selectedId;
    setSelectedId(id);

    try {
      await setElectronSelectedPrinterId(id);
    } catch (err) {
      setSelectedId(previousId);
      toast.error("Could not save printer selection", {
        description: err instanceof Error ? err.message : String(err),
      });
      return;
    }

    if (match) {
      toast.success(`Printer set to ${match.name}`);
    }
  };

  const handleTestPrint = async () => {
    if (!selectedPrinter || !api) return;
    setTesting(true);
    try {
      const result = await api.testPrint(toPrinterTarget(selectedPrinter));
      if (result.success) {
        toast.success("Test print sent");
      } else {
        toast.error("Test print failed", { description: result.error });
      }
    } catch (err) {
      toast.error("Test print failed", {
        description: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="rounded-lg border p-6">
      <h3 className="text-lg font-semibold mb-4">Workstation printer</h3>
      <p className="text-sm text-muted-foreground mb-4">
        Select the printer connected to this workstation. This setting is stored
        locally and applies to all users on this machine.
      </p>
      <div className="flex flex-col gap-4 max-w-sm">
        <Select
          aria-label="Workstation printer"
          selectedKey={selectedId}
          onSelectionChange={handleSelectionChange}
          disabledKeys={disabledPrinterIds}
        >
          <Label>Printer</Label>
          <SelectTrigger isClearable />
          <SelectList items={printerOptions}>
            {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
          </SelectList>
        </Select>

        {selectedPrinter && (
          <div className="text-sm text-muted-foreground">
            {selectedPrinter.type === "NETWORK"
              ? `${selectedPrinter.address}:${selectedPrinter.port ?? 9100}`
              : `USB ${selectedPrinter.usbVendorId}:${selectedPrinter.usbProductId}`}
          </div>
        )}

        <Button
          variant="outline"
          onPress={handleTestPrint}
          isDisabled={!selectedPrinter}
          isPending={testing}
          className="w-fit"
        >
          Test print
        </Button>
      </div>
    </div>
  );
}
